import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil } from "lucide-react";

/**
 * Universal inline-edit wrapper.
 *  - Idle: renders `value` as plain text with a subtle hover affordance.
 *  - Click → input mode.
 *  - Blur or Enter → call onSave(newValue); Esc → cancel.
 *
 * Props:
 *  value, onSave(next), type="text"|"number"|"textarea", placeholder, className,
 *  ariaLabel, "data-testid", maxLength, formatter(val)=>display
 */
export const EditableField = ({
  value,
  onSave,
  type = "text",
  placeholder = "—",
  className = "",
  ariaLabel,
  formatter,
  maxLength,
  "data-testid": testId,
  align = "left",
  inputClassName = "",
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const commit = async () => {
    setEditing(false);
    const next = type === "number" ? (draft === "" ? null : Number(draft)) : draft;
    if (next === value) return;
    try { await onSave?.(next); } catch { /* parent should toast */ }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && type !== "textarea") { e.preventDefault(); commit(); }
    if (e.key === "Enter" && type === "textarea" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); commit(); }
    if (e.key === "Escape") { setEditing(false); setDraft(value ?? ""); }
  };

  if (editing) {
    const Comp = type === "textarea" ? Textarea : Input;
    return (
      <Comp
        ref={inputRef}
        autoFocus
        type={type === "number" ? "number" : "text"}
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid={testId ? `${testId}-input` : undefined}
        className={`ring-1 ring-primary/60 focus-visible:ring-primary/80 ${inputClassName}`}
      />
    );
  }

  const display = formatter ? formatter(value) : (value ?? "");
  const isEmpty = display === "" || display === null || display === undefined;
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={ariaLabel}
      data-testid={testId}
      className={`group/edit relative inline-flex items-center gap-1.5 text-${align} rounded-sm border border-transparent hover:border-border/60 hover:bg-muted/20 transition-colors px-1 ${className}`}
    >
      <span className={`${isEmpty ? "text-muted-foreground/50 italic" : ""} w-full`}>
        {isEmpty ? placeholder : display}
      </span>
      <Pencil className="w-3 h-3 text-muted-foreground/0 group-hover/edit:text-muted-foreground/60 transition-colors flex-shrink-0" />
    </button>
  );
};

export default EditableField;
